import { useCallback, useEffect, useRef, useState } from 'react';

import { getChildTenants, getTickets, setupJira } from '../../../hooks/jira';
import { ChildTenant, TicketSummary } from '../../../types/responses/Jira';

const POLL_MS = 120000;

export const statsOf = (tickets: TicketSummary[]) => ({
  total: tickets.length,
  open: tickets.filter((t) => t.statusCategory === 'open').length,
  inProgress: tickets.filter((t) => t.statusCategory === 'inProgress').length,
  done: tickets.filter((t) => t.statusCategory === 'done').length,
});

// O backend pagina /jira/tickets por um token opaco (pageToken) que so sabe andar pra frente — nao
// da pra "pular" pra uma pagina arbitraria so com o numero, e nunca devolve um total. Para usar o
// mesmo molecule <Pagination> (que exige currentPage/totalCount) sem inventar uma capacidade que o
// backend nao tem, mantemos um cache local pagina -> token (igual useInternalTickets.ts): toda vez
// que a pagina N responde com sucesso e nao e a ultima, guardamos o token dela como "o token pra
// pedir a pagina N+1" (tokenForPage). Isso permite voltar pra uma pagina ja visitada (o token dela
// ja esta no cache) sem reconsultar o servidor em ordem; so a fronteira (maxKnownPage + 1) de fato
// avanca a consulta. Pedir uma pagina cujo token nao foi descoberto ainda (p.ex. apos um filtro novo
// zerar o cache) e um no-op defensivo — nunca deveria acontecer porque a UI (Pagination) so oferece
// paginas ja conhecidas ou a propria fronteira. totalCount e so maxKnownPage (+1 se a fronteira ainda
// tiver proxima pagina), nunca um total real que o backend nunca devolve; `hasNext` continua exposto
// como atalho derivado.
interface Pagination {
  page: number;
  maxKnownPage: number;
  frontierHasNext: boolean;
}

const INITIAL_PAGINATION: Pagination = { page: 1, maxKnownPage: 1, frontierHasNext: false };

export const useTickets = () => {
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [status, setStatus] = useState<'all' | 'open' | 'closed'>('all');
  const [childFilter, setChildFilter] = useState<number | undefined>(undefined);
  const [children, setChildren] = useState<ChildTenant[]>([]);
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const ready = useRef(false);
  const mounted = useRef(true);
  const requestSeq = useRef(0);
  // pagina -> token necessario pra pedir aquela pagina; a pagina 1 nunca precisa de token.
  const tokenForPage = useRef<Map<number, string | undefined>>(new Map([[1, undefined]]));

  const handle = useCallback((e: any) => {
    const code = e?.response?.status;
    if (code === 403) setForbidden(true);
    else if (code === 409) setError('not-configured');
    else setError('generic');
  }, []);

  // `rethrow`: a inicializacao trata o erro ela mesma (409 -> setup). Devolve false se a resposta
  // foi descartada por ter chegado fora de ordem, ou se a pagina pedida nao tem token conhecido
  // (no-op defensivo, ver comentario acima).
  // `preserveFrontier`: só true quando p===1 vem de um clique explícito no botão "1" do
  // <Pagination> (ver setPage) — nesse caso a fronteira já conhecida (maxKnownPage/frontierHasNext)
  // não deve ser jogada fora, senão o usuário perde o número de páginas que já tinha visto. Troca de
  // filtro/polling/refresh continuam resetando (dataset pode ter mudado de verdade).
  const load = useCallback(
    async (p: number, rethrow = false, preserveFrontier = false): Promise<boolean> => {
      // Recarregar a pagina 1 (filtro novo, polling ou refresh) invalida qualquer token conhecido na
      // hora: nenhuma pagina alem da 1 vale enquanto essa resposta nao chegar, com sucesso ou nao —
      // mesmo efeito do reset preemptivo que useInternalTickets.ts faz antes do fetch.
      if (p === 1 && !preserveFrontier) {
        tokenForPage.current = new Map([[1, undefined]]);
        setPagination(INITIAL_PAGINATION);
      } else if (!tokenForPage.current.has(p)) {
        return false;
      }
      const token = p === 1 ? undefined : tokenForPage.current.get(p);
      const seq = ++requestSeq.current; // descarta respostas fora de ordem
      try {
        const r = await getTickets({ status, childTenantPk: childFilter, pageToken: token });
        if (!mounted.current || seq !== requestSeq.current) return false;
        setTickets(r.items);
        if (!r.isLastPage) tokenForPage.current.set(p + 1, r.nextPageToken ?? undefined);
        setPagination((prev) => {
          if (p === 1 && preserveFrontier) return { ...prev, page: p };
          const isFrontier = p === 1 || p >= prev.maxKnownPage;
          return {
            page: p,
            maxKnownPage: p === 1 ? 1 : Math.max(prev.maxKnownPage, p),
            frontierHasNext: isFrontier ? !r.isLastPage : prev.frontierHasNext,
          };
        });
        setError(null);
        return true;
      } catch (e) {
        if (rethrow) throw e;
        if (mounted.current && seq === requestSeq.current) handle(e);
        return false;
      }
    },
    [status, childFilter, handle],
  );

  // Sempre a versao mais recente de load: a inicializacao assincrona e o polling nao usam filtros velhos.
  const loadRef = useRef(load);
  loadRef.current = load;

  // Inicializacao: filhos elegiveis + primeira pagina em paralelo. O setup (vincula a organizacao
  // do Jira) so roda quando a lista responde 409 (tenant ainda sem organizacao), seguido de UMA
  // nova tentativa. Qualquer outro erro segue o tratamento normal (403 -> forbidden; 5xx -> erro
  // com "Tentar novamente", que refaz a inicializacao). So marca `ready` ao terminar.
  const initializing = useRef(false);
  const init = useCallback(async () => {
    if (initializing.current) return;
    initializing.current = true;
    const firstPage = async () => {
      const used = loadRef.current;
      await used(1, true);
      // filtro mudou durante a carga inicial: recarrega com o filtro atual
      if (mounted.current && loadRef.current !== used) await loadRef.current(1, true);
    };
    try {
      const kidsP = getChildTenants().catch(() => [] as ChildTenant[]);
      try {
        await firstPage();
      } catch (e: any) {
        if (e?.response?.status !== 409) throw e;
        await setupJira();
        if (!mounted.current) return;
        await firstPage();
      }
      const kids = await kidsP;
      if (!mounted.current) return;
      setChildren(kids);
      setForbidden(false);
      ready.current = true;
    } catch (e) {
      if (mounted.current) handle(e);
    } finally {
      initializing.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [handle]);

  useEffect(() => {
    mounted.current = true;
    init();
    return () => {
      mounted.current = false;
    };
  }, [init]);

  // recarrega ao mudar filtros (durante a inicializacao nao: ela ja termina com o filtro atual)
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (ready.current) loadRef.current(1);
  }, [status, childFilter]);

  // polling so com a aba visivel; limpo ao desmontar
  useEffect(() => {
    const id = setInterval(() => {
      if (ready.current && document.visibilityState === 'visible') loadRef.current(1);
    }, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const totalCount = pagination.maxKnownPage + (pagination.frontierHasNext ? 1 : 0);

  return {
    tickets,
    stats: statsOf(tickets),
    status,
    setStatus,
    childFilter,
    setChildFilter,
    children,
    loading,
    error,
    forbidden,
    page: pagination.page,
    pageSize: 1,
    totalCount,
    hasNext: pagination.page < totalCount,
    refresh: () => (ready.current ? loadRef.current(1) : init()),
    setPage: (p: number) => (ready.current ? loadRef.current(p, false, true) : undefined),
  };
};
